import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  Tag,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Statistic,
  Divider,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useDepartmentKPIs, useCreateDepartmentKPI, useUpdateDepartmentKPI, useDeleteDepartmentKPI } from '../../hooks/useKPIs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface DepartmentKPI {
  id?: string;
  kpi_code: string;
  kpi_name: string;
  description?: string;
  department_id?: string;
  measurement_unit?: string;
  target_type: string;
  calculation_method?: string;
  data_source?: string;
  measurement_frequency: string;
  default_target_value?: number;
  threshold_warning?: number;
  threshold_critical?: number;
  display_order?: number;
  is_primary?: boolean;
  is_active?: boolean;
}

// Pre-defined KPI templates for composite panel cladding, stainless works, and glass processing business
const KPI_TEMPLATES = [
  {
    category: 'Production Quality - Fabrication',
    kpis: [
      {
        kpi_code: 'PROD-FPY',
        kpi_name: 'First Pass Yield',
        description: 'Percentage of products passing inspection on first attempt',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Daily',
        default_target_value: 95,
        threshold_warning: 90,
        threshold_critical: 85,
      },
      {
        kpi_code: 'PROD-DEFECT',
        kpi_name: 'Defect Rate',
        description: 'Number of defects per 1000 units produced',
        measurement_unit: 'defects/1000',
        target_type: 'Numeric',
        measurement_frequency: 'Daily',
        default_target_value: 5,
        threshold_warning: 8,
        threshold_critical: 10,
      },
      {
        kpi_code: 'PROD-REWORK',
        kpi_name: 'Rework Rate',
        description: 'Percentage of items requiring rework',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 3,
        threshold_warning: 5,
        threshold_critical: 8,
      },
    ],
  },
  {
    category: 'Curtain Wall & Glazing Systems',
    kpis: [
      {
        kpi_code: 'CW-SEAL',
        kpi_name: 'Curtain Wall Seal Quality',
        description: 'Water/air tightness test pass rate for curtain wall systems',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Per Project',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
      {
        kpi_code: 'CW-ALIGN',
        kpi_name: 'Panel Alignment Tolerance',
        description: 'Percentage of panels within alignment tolerance (±2mm)',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 98,
        threshold_warning: 95,
        threshold_critical: 90,
      },
      {
        kpi_code: 'GLAZ-BREAK',
        kpi_name: 'Glass Breakage Rate',
        description: 'Number of glass units broken per 100 installed',
        measurement_unit: 'per 100',
        target_type: 'Numeric',
        measurement_frequency: 'Monthly',
        default_target_value: 0.5,
        threshold_warning: 1.0,
        threshold_critical: 2.0,
      },
      {
        kpi_code: 'GLAZ-SEAL',
        kpi_name: 'Glazing Seal Integrity',
        description: 'Percentage of glazing seals passing pressure test',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Per Project',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
    ],
  },
  {
    category: 'Welding & Stainless Steel Works',
    kpis: [
      {
        kpi_code: 'WELD-PASS',
        kpi_name: 'Weld Pass Rate (First Time)',
        description: 'Percentage of welds passing visual inspection first time',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Daily',
        default_target_value: 98,
        threshold_warning: 95,
        threshold_critical: 90,
      },
      {
        kpi_code: 'WELD-NDT',
        kpi_name: 'NDT Pass Rate',
        description: 'Percentage of welds passing NDT (LPT/MPI/UT)',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 99,
        threshold_warning: 97,
        threshold_critical: 95,
      },
      {
        kpi_code: 'SS-FINISH',
        kpi_name: 'Stainless Steel Finish Quality',
        description: 'Percentage of SS items with acceptable surface finish',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 98,
        threshold_warning: 95,
        threshold_critical: 92,
      },
      {
        kpi_code: 'WELD-CERT',
        kpi_name: 'Welder Certification Currency',
        description: 'Percentage of welders with current certifications',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Monthly',
        default_target_value: 100,
        threshold_warning: 95,
        threshold_critical: 90,
      },
    ],
  },
  {
    category: 'Material & Cladding',
    kpis: [
      {
        kpi_code: 'MAT-RECV',
        kpi_name: 'Material Receiving Acceptance',
        description: 'Percentage of materials accepted on first inspection',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 98,
        threshold_warning: 95,
        threshold_critical: 90,
      },
      {
        kpi_code: 'CLAD-FLAT',
        kpi_name: 'Cladding Panel Flatness',
        description: 'Percentage of panels meeting flatness tolerance',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Daily',
        default_target_value: 99,
        threshold_warning: 97,
        threshold_critical: 95,
      },
      {
        kpi_code: 'CLAD-COLOR',
        kpi_name: 'Color Match Rate',
        description: 'Percentage of panels matching approved color sample',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Per Batch',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
    ],
  },
  {
    category: 'Installation & Site Quality',
    kpis: [
      {
        kpi_code: 'INST-PLUMB',
        kpi_name: 'Installation Plumbness',
        description: 'Percentage of installations within plumbness tolerance',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
      {
        kpi_code: 'INST-DAMAGE',
        kpi_name: 'Installation Damage Rate',
        description: 'Percentage of items damaged during installation',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 1,
        threshold_warning: 2,
        threshold_critical: 5,
      },
      {
        kpi_code: 'SITE-SAFETY',
        kpi_name: 'Site Safety Compliance',
        description: 'Percentage compliance with safety protocols on site',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Daily',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
    ],
  },
  {
    category: 'Inspection & Testing',
    kpis: [
      {
        kpi_code: 'INSP-COMPLETION',
        kpi_name: 'Inspection Completion Rate',
        description: 'Percentage of scheduled inspections completed on time',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 100,
        threshold_warning: 95,
        threshold_critical: 90,
      },
      {
        kpi_code: 'INSP-PASS',
        kpi_name: 'Inspection Pass Rate',
        description: 'Percentage of inspections passed',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 98,
        threshold_warning: 95,
        threshold_critical: 90,
      },
      {
        kpi_code: 'HOLD-RELEASE',
        kpi_name: 'Hold Point Release Time',
        description: 'Average hours to release hold points after inspection',
        measurement_unit: 'hours',
        target_type: 'Numeric',
        measurement_frequency: 'Weekly',
        default_target_value: 4,
        threshold_warning: 8,
        threshold_critical: 24,
      },
    ],
  },
  {
    category: 'Dimensional Accuracy',
    kpis: [
      {
        kpi_code: 'DIM-TOL',
        kpi_name: 'Dimensional Tolerance Compliance',
        description: 'Percentage of measurements within drawing tolerances',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Daily',
        default_target_value: 98,
        threshold_warning: 95,
        threshold_critical: 90,
      },
      {
        kpi_code: 'DIM-CRIT',
        kpi_name: 'Critical Dimension Accuracy',
        description: 'Percentage of critical dimensions within ±0.5mm',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Daily',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
    ],
  },
  {
    category: 'NCR & Corrective Action',
    kpis: [
      {
        kpi_code: 'NCR-CLOSURE',
        kpi_name: 'NCR Closure Time',
        description: 'Average days to close NCRs',
        measurement_unit: 'days',
        target_type: 'Numeric',
        measurement_frequency: 'Monthly',
        default_target_value: 7,
        threshold_warning: 10,
        threshold_critical: 14,
      },
      {
        kpi_code: 'NCR-RECURRENCE',
        kpi_name: 'NCR Recurrence Rate',
        description: 'Percentage of recurring NCRs (same issue within 6 months)',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Monthly',
        default_target_value: 5,
        threshold_warning: 10,
        threshold_critical: 15,
      },
      {
        kpi_code: 'CAPA-EFFECTIVE',
        kpi_name: 'CAPA Effectiveness Rate',
        description: 'Percentage of CAPAs proven effective on verification',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Monthly',
        default_target_value: 95,
        threshold_warning: 90,
        threshold_critical: 85,
      },
    ],
  },
  {
    category: 'Aluminium & Door/Window Systems',
    kpis: [
      {
        kpi_code: 'ALU-CUT',
        kpi_name: 'Aluminium Cutting Accuracy',
        description: 'Percentage of aluminium profiles cut within ±0.5mm tolerance',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Daily',
        default_target_value: 99,
        threshold_warning: 97,
        threshold_critical: 95,
      },
      {
        kpi_code: 'DOOR-OPER',
        kpi_name: 'Door Operation Quality',
        description: 'Percentage of doors passing smooth operation test',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
      {
        kpi_code: 'WIN-SEAL',
        kpi_name: 'Window Seal Integrity',
        description: 'Percentage of windows passing water penetration test',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Per Project',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
    ],
  },
  {
    category: 'Glass Processing & Handling',
    kpis: [
      {
        kpi_code: 'GLASS-EDGE',
        kpi_name: 'Glass Edge Quality',
        description: 'Percentage of glass edges meeting finish standards',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Daily',
        default_target_value: 99,
        threshold_warning: 97,
        threshold_critical: 95,
      },
      {
        kpi_code: 'GLASS-DAMAGE',
        kpi_name: 'Glass Handling Damage Rate',
        description: 'Percentage of glass units damaged during handling/storage',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Monthly',
        default_target_value: 0.5,
        threshold_warning: 1.0,
        threshold_critical: 2.0,
      },
      {
        kpi_code: 'GLASS-CLEAN',
        kpi_name: 'Glass Cleanliness Standard',
        description: 'Percentage of glass units meeting cleanliness inspection',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
    ],
  },
  {
    category: 'Surface Treatment & Finishing',
    kpis: [
      {
        kpi_code: 'POWDER-COAT',
        kpi_name: 'Powder Coating Quality',
        description: 'Percentage of coated items passing thickness & adhesion test',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Daily',
        default_target_value: 98,
        threshold_warning: 95,
        threshold_critical: 92,
      },
      {
        kpi_code: 'SS-POLISH',
        kpi_name: 'Stainless Steel Polishing Quality',
        description: 'Percentage of SS items achieving specified finish (2B, BA, No.4, Mirror)',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Daily',
        default_target_value: 98,
        threshold_warning: 95,
        threshold_critical: 90,
      },
      {
        kpi_code: 'ANOD-QUALITY',
        kpi_name: 'Anodizing Quality',
        description: 'Percentage of anodized aluminium meeting color & thickness specs',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Per Batch',
        default_target_value: 99,
        threshold_warning: 97,
        threshold_critical: 95,
      },
    ],
  },
  {
    category: 'Installation Quality',
    kpis: [
      {
        kpi_code: 'INST-SEALANT',
        kpi_name: 'Sealant Application Quality',
        description: 'Percentage of sealant joints meeting visual & adhesion standards',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 98,
        threshold_warning: 95,
        threshold_critical: 90,
      },
      {
        kpi_code: 'INST-ANCHOR',
        kpi_name: 'Anchor/Fixing Integrity',
        description: 'Percentage of anchors passing pull-out test',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Per Project',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
      {
        kpi_code: 'INST-LEVEL',
        kpi_name: 'Installation Levelness',
        description: 'Percentage of installed items within level tolerance (±2mm/m)',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Weekly',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
    ],
  },
  {
    category: 'Client & Handover',
    kpis: [
      {
        kpi_code: 'CLIENT-SNAG',
        kpi_name: 'Client Snagging Rate',
        description: 'Number of snags identified by client per project',
        measurement_unit: 'snags',
        target_type: 'Numeric',
        measurement_frequency: 'Per Project',
        default_target_value: 5,
        threshold_warning: 10,
        threshold_critical: 20,
      },
      {
        kpi_code: 'HANDOVER-COMP',
        kpi_name: 'Handover Completeness',
        description: 'Percentage of handover documentation complete on first submission',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Per Project',
        default_target_value: 100,
        threshold_warning: 95,
        threshold_critical: 90,
      },
      {
        kpi_code: 'CLIENT-SAT',
        kpi_name: 'Client Satisfaction Score',
        description: 'Average client satisfaction rating (1-10 scale)',
        measurement_unit: 'score',
        target_type: 'Rating',
        measurement_frequency: 'Per Project',
        default_target_value: 9,
        threshold_warning: 8,
        threshold_critical: 7,
      },
    ],
  },
  {
    category: 'Equipment & Calibration',
    kpis: [
      {
        kpi_code: 'EQUIP-CALIB',
        kpi_name: 'Equipment Calibration Currency',
        description: 'Percentage of measuring equipment with valid calibration',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Monthly',
        default_target_value: 100,
        threshold_warning: 98,
        threshold_critical: 95,
      },
      {
        kpi_code: 'EQUIP-DOWN',
        kpi_name: 'Equipment Downtime',
        description: 'Percentage of production time lost to equipment issues',
        measurement_unit: '%',
        target_type: 'Percentage',
        measurement_frequency: 'Monthly',
        default_target_value: 2,
        threshold_warning: 5,
        threshold_critical: 10,
      },
    ],
  },
];

const DepartmentKPIsPage: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [editingKPI, setEditingKPI] = useState<DepartmentKPI | null>(null);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [templateSearchText, setTemplateSearchText] = useState('');
  const [form] = Form.useForm();

  const { data: kpis = [], isLoading, refetch } = useDepartmentKPIs();
  const createMutation = useCreateDepartmentKPI();
  const updateMutation = useUpdateDepartmentKPI();
  const deleteMutation = useDeleteDepartmentKPI();

  const targetTypes = ['Numeric', 'Percentage', 'Boolean', 'Rating'];
  const frequencies = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Per Job', 'Per Project'];

  const handleCreate = () => {
    setEditingKPI(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (kpi: any) => {
    setEditingKPI(kpi);
    form.setFieldsValue(kpi);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('KPI deleted successfully');
      refetch();
    } catch (error) {
      console.error('Error deleting KPI:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingKPI?.id) {
        await updateMutation.mutateAsync({ id: editingKPI.id, data: values });
        message.success('KPI updated successfully');
      } else {
        await createMutation.mutateAsync(values);
        message.success('KPI created successfully');
      }
      setIsModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error) {
      console.error('Error saving KPI:', error);
    }
  };

  const handleAutoAddKPIs = () => {
    setSelectedTemplates([]);
    setTemplateSearchText('');
    setIsTemplateModalVisible(true);
  };

  const handleAddSelectedTemplates = async () => {
    try {
      const allTemplates = KPI_TEMPLATES.flatMap(cat => cat.kpis);
      const templatesToAdd = allTemplates.filter((_, index) => 
        selectedTemplates.includes(index.toString())
      );

      for (const template of templatesToAdd) {
        await createMutation.mutateAsync(template);
      }

      message.success(`${templatesToAdd.length} KPIs added successfully`);
      setIsTemplateModalVisible(false);
      setSelectedTemplates([]);
      refetch();
    } catch (error) {
      console.error('Error adding template KPIs:', error);
      message.error('Failed to add KPIs');
    }
  };

  const stats = {
    total: kpis.length,
    active: kpis.filter((k: any) => k.is_active).length,
    primary: kpis.filter((k: any) => k.is_primary).length,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'KPI Code',
      dataIndex: 'kpi_code',
      key: 'kpi_code',
      width: 120,
    },
    {
      title: 'KPI Name',
      dataIndex: 'kpi_name',
      key: 'kpi_name',
      width: 200,
    },
    {
      title: 'Unit',
      dataIndex: 'measurement_unit',
      key: 'measurement_unit',
      width: 100,
    },
    {
      title: 'Type',
      dataIndex: 'target_type',
      key: 'target_type',
      width: 120,
    },
    {
      title: 'Frequency',
      dataIndex: 'measurement_frequency',
      key: 'measurement_frequency',
      width: 120,
    },
    {
      title: 'Target',
      dataIndex: 'default_target_value',
      key: 'default_target_value',
      width: 100,
      render: (value: number, record: any) => value ? `${value} ${record.measurement_unit || ''}` : 'N/A',
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_: any, record: any) => (
        <Space>
          {record.is_primary && <Tag color="gold">Primary</Tag>}
          <Tag color={record.is_active ? 'success' : 'default'}>
            {record.is_active ? 'Active' : 'Inactive'}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete KPI"
            description="Are you sure you want to delete this KPI?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Department KPIs</Title>
      <Text type="secondary">Define and manage Key Performance Indicators for quality management</Text>

      <Row gutter={16} style={{ marginTop: 24, marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic title="Total KPIs" value={stats.total} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Active KPIs" value={stats.active} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Primary KPIs" value={stats.primary} valueStyle={{ color: '#145A54' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create KPI
          </Button>
          <Button icon={<ThunderboltOutlined />} onClick={handleAutoAddKPIs}>
            Auto-Add KPI Templates
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={kpis}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingKPI ? 'Edit KPI' : 'Create KPI'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        width={700}
        okText="Save"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="kpi_code" label="KPI Code" rules={[{ required: true }]}>
                <Input placeholder="e.g., PROD-001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="kpi_name" label="KPI Name" rules={[{ required: true }]}>
                <Input placeholder="e.g., First Pass Yield" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Describe what this KPI measures..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="target_type" label="Target Type" rules={[{ required: true }]}>
                <Select placeholder="Select type">
                  {targetTypes.map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="measurement_unit" label="Measurement Unit">
                <Input placeholder="e.g., %, pcs, days" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="measurement_frequency" label="Frequency" rules={[{ required: true }]}>
                <Select placeholder="Select frequency">
                  {frequencies.map(freq => (
                    <Option key={freq} value={freq}>{freq}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="default_target_value" label="Default Target Value">
                <InputNumber style={{ width: '100%' }} placeholder="Target value" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="threshold_warning" label="Warning Threshold">
                <InputNumber style={{ width: '100%' }} placeholder="Warning level" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="threshold_critical" label="Critical Threshold">
                <InputNumber style={{ width: '100%' }} placeholder="Critical level" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="is_primary" label="Primary KPI" valuePropName="checked">
                <Select>
                  <Option value={true}>Yes</Option>
                  <Option value={false}>No</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
                <Select>
                  <Option value={true}>Active</Option>
                  <Option value={false}>Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Template Selection Modal */}
      <Modal
        title={
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Title level={4} style={{ margin: 0 }}>Auto-Add KPI Templates</Title>
            <Text type="secondary">{KPI_TEMPLATES.reduce((sum, cat) => sum + cat.kpis.length, 0)} templates available across {KPI_TEMPLATES.length} categories</Text>
          </Space>
        }
        open={isTemplateModalVisible}
        onCancel={() => {
          setIsTemplateModalVisible(false);
          setTemplateSearchText('');
        }}
        onOk={handleAddSelectedTemplates}
        width={900}
        okText={`Add ${selectedTemplates.length} Selected KPI${selectedTemplates.length !== 1 ? 's' : ''}`}
        okButtonProps={{ disabled: selectedTemplates.length === 0 }}
      >
        <Alert
          message="Select KPI Templates"
          description="Choose pre-defined KPIs tailored for curtain wall, glazing, stainless steel, and cladding works. You can customize them after adding."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* Search Box */}
        <Input
          placeholder="Search templates by name, code, or category..."
          prefix={<SearchOutlined />}
          value={templateSearchText}
          onChange={(e) => setTemplateSearchText(e.target.value)}
          allowClear
          style={{ marginBottom: 16 }}
        />

        {KPI_TEMPLATES.filter(category => {
          if (!templateSearchText) return true;
          const searchLower = templateSearchText.toLowerCase();
          return category.category.toLowerCase().includes(searchLower) ||
                 category.kpis.some(kpi => 
                   kpi.kpi_name.toLowerCase().includes(searchLower) ||
                   kpi.kpi_code.toLowerCase().includes(searchLower) ||
                   kpi.description.toLowerCase().includes(searchLower)
                 );
        }).map((category, catIndex) => {
          const filteredKPIs = category.kpis.filter(kpi => {
            if (!templateSearchText) return true;
            const searchLower = templateSearchText.toLowerCase();
            return kpi.kpi_name.toLowerCase().includes(searchLower) ||
                   kpi.kpi_code.toLowerCase().includes(searchLower) ||
                   kpi.description.toLowerCase().includes(searchLower);
          });
          
          if (filteredKPIs.length === 0) return null;
          
          return (
          <div key={catIndex}>
            <Divider orientation="left">
              <Space>
                <Text strong>{category.category}</Text>
                <Tag color="blue">{filteredKPIs.length} template{filteredKPIs.length !== 1 ? 's' : ''}</Tag>
              </Space>
            </Divider>
            <Space direction="vertical" style={{ width: '100%' }}>
              {filteredKPIs.map((kpi, kpiIndex) => {
                const originalKpiIndex = category.kpis.indexOf(kpi);
                const globalIndex = KPI_TEMPLATES.slice(0, catIndex)
                  .reduce((sum, cat) => sum + cat.kpis.length, 0) + originalKpiIndex;
                
                return (
                  <Card
                    key={globalIndex}
                    size="small"
                    style={{ 
                      cursor: 'pointer',
                      border: selectedTemplates.includes(globalIndex.toString()) ? '2px solid #145A54' : undefined 
                    }}
                    onClick={() => {
                      const idxStr = globalIndex.toString();
                      setSelectedTemplates(prev =>
                        prev.includes(idxStr) 
                          ? prev.filter(id => id !== idxStr)
                          : [...prev, idxStr]
                      );
                    }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      <Space>
                        <Tag color="blue">{kpi.kpi_code}</Tag>
                        <Text strong>{kpi.kpi_name}</Text>
                        {selectedTemplates.includes(globalIndex.toString()) && (
                          <CheckCircleOutlined style={{ color: '#145A54' }} />
                        )}
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>{kpi.description}</Text>
                      <Space size="large">
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Target: {kpi.default_target_value} {kpi.measurement_unit}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Frequency: {kpi.measurement_frequency}
                        </Text>
                      </Space>
                    </Space>
                  </Card>
                );
              })}
            </Space>
          </div>
        )}).filter(Boolean)}
        
        {templateSearchText && KPI_TEMPLATES.every(cat => 
          !cat.category.toLowerCase().includes(templateSearchText.toLowerCase()) &&
          !cat.kpis.some(kpi => 
            kpi.kpi_name.toLowerCase().includes(templateSearchText.toLowerCase()) ||
            kpi.kpi_code.toLowerCase().includes(templateSearchText.toLowerCase()) ||
            kpi.description.toLowerCase().includes(templateSearchText.toLowerCase())
          )
        ) && (
          <Alert
            message="No Templates Found"
            description={`No KPI templates match "${templateSearchText}". Try a different search term.`}
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Modal>
    </div>
  );
};

export default DepartmentKPIsPage;
